import { Alert, Box, IconButton, Menu, MenuItem, useTheme, Snackbar, ListItemIcon, ListItemText, MenuList } from '@mui/material';
import { FiEdit, FiThumbsDown, FiTrash2} from 'react-icons/fi';
import { LuCopy, LuCopyMinus } from "react-icons/lu";
import { HiDotsVertical } from "react-icons/hi";
import { useState } from 'react';
import { unEscapeHTML, stripMarkdown } from '../../Utils/text_functions.js'
import { marked } from 'marked';


export const ChatActionButtons = ({ 
  message, 
  isLastMeMessage,
  clickHandlerDelete, 
  clickHandlerDown, 
  clickHandlerEdit,
  setChatInput,
  isBotLoggedIn,
  deepAgentFinalResponse
}) => {
  const theme = useTheme()
  const themeColor = theme.palette.text.primary
  const [downVote, setDownVote] = useState(message.embedding_down === 'true' ? true : false)
  const [copyState, setCopyState] = useState(false)

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [openSnackbar, setOpenSnackbar] = useState(undefined);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(undefined);
  };

  const getMessageToCopy = () => {
    if ( deepAgentFinalResponse && deepAgentFinalResponse !== '' ){
      return deepAgentFinalResponse
    } else if ( message.message_orig !== undefined && message.message_orig !== '' ){
      return message.message_orig
    } else {
      return message.message
    }
  }

  const copyToClipboard = () => {
    setAnchorEl(null)
    const messageToCopy = getMessageToCopy()
    if(messageToCopy === undefined) {
      setOpenSnackbar({
        message: 'Failed to copy to clipboard',
        severity: 'error'
      });
      return
    }
    navigator.clipboard.writeText(unEscapeHTML(messageToCopy));
    setOpenSnackbar({
      message: 'Copied to clipboard',
      severity: 'success'
    });
  }
  const copyToClipboardNoMarkdown = () => {
    setAnchorEl(null)
    const messageToCopy = getMessageToCopy()
    if(messageToCopy === undefined) {
      setOpenSnackbar({
        message: 'Failed to copy to clipboard',
        severity: 'error'
      });
      return
    }
    const messageParsed = stripMarkdown(unEscapeHTML(messageToCopy));
    navigator.clipboard.writeText(messageParsed);
    setOpenSnackbar({
      message: 'Copied to clipboard',
      severity: 'success'
    });
  }

  const copyToClipboardDOCX = async () => {
    setAnchorEl(null);

    const md = getMessageToCopy();
    if (md === undefined) {
      setOpenSnackbar({ message: 'Failed to copy to clipboard', severity: 'error' });
      return;
    }

    // 1. Plain text (what you already had)
    const plain = stripMarkdown(md);

    // 2. Rich HTML that Word understands
    //    marked already escapes HTML; we add a wrapping <div> to be safe.
    const html =
      `<div style="font-family:Segoe UI,Arial,Helvetica,sans-serif">` +
      marked.parse(md) +
      `</div>`;

    try {
      // Modern async Clipboard API
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plain], { type: 'text/plain' }),
          'text/html' : new Blob([html],  { type: 'text/html'  })
        })
      ]);

      setOpenSnackbar({ message: 'Copied to clipboard (Word-ready)!', severity: 'success' });
    } catch (err) {
      console.error(err);
      setOpenSnackbar({ message: 'Failed to copy to clipboard', severity: 'error' });
    }
  };


  const handleDeleteEvent = (event) => {
    setAnchorEl(null)
    if(message.id) {
      clickHandlerDelete(message.id)
    }
    else {
      clickHandlerDelete(message)
    }
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <IconButton
        id="action-menu-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        size='small'
      >
        <HiDotsVertical color={themeColor} />
      </IconButton>
      {open && (
        <Menu
          id="action-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'action-menu-button',
          }}
        >
          <MenuList style={{paddingTop: 1, paddingBottom: 1}} dense sx={{fontSize: 10}}>
            {message.user === 'gpt' && message.id != null && !isBotLoggedIn() && !message.teach && (
              <MenuItem
                onClick={
                  () => {
                    setDownVote(true)
                    setAnchorEl(null)
                    setOpenSnackbar({
                      message: 'Thank you for your feedback!',
                      severity: 'success'
                    });
                    clickHandlerDown(message.id)
                  }
                } 
                aria-label="thumbs down"
              >
                <ListItemIcon>
                  <FiThumbsDown color={themeColor} size={16}/>
                </ListItemIcon>
                <ListItemText primary="Downvote" />
              </MenuItem>
            )}
            {message.user === 'gpt' && message.id != null && (
              <MenuItem onClick={copyToClipboard} aria-label="copy">
                <ListItemIcon>
                  <LuCopy color={themeColor} style={{backgroundColor: copyState ? 'burlywood' : 'transparent'}} size={16}/>
                </ListItemIcon>
                <ListItemText primary="Copy Raw" />
              </MenuItem>
            )}
            {message.user === 'gpt' && message.id != null && (
              <MenuItem onClick={copyToClipboardDOCX} aria-label="copy">
                <ListItemIcon>
                  <LuCopy color={themeColor} style={{backgroundColor: copyState ? 'burlywood' : 'transparent'}} size={16}/>
                </ListItemIcon>
                <ListItemText primary="Copy to DOCX" />
              </MenuItem>
            )}
            {message.user === 'gpt' && message.id != null && (
              <MenuItem onClick={copyToClipboardNoMarkdown} aria-label="copy">
                <ListItemIcon>
                  <LuCopyMinus color={themeColor} style={{backgroundColor: copyState ? 'burlywood' : 'transparent'}} size={16}/>
                </ListItemIcon>
                <ListItemText primary="Copy Cleaned" />
              </MenuItem>
            )}
            {clickHandlerEdit && message.user === 'me' && message.id != null && (!message.deep_agent_id) && (
              <MenuItem onClick={() => clickHandlerEdit(message.id, message.message, setChatInput)} aria-label="edit">
                <ListItemIcon>
                  <FiEdit color={themeColor} size={16} />
                </ListItemIcon>
                <ListItemText primary="Edit" />
              </MenuItem>
            )}
            <MenuItem onClick={() => handleDeleteEvent()} aria-label="delete">
              <ListItemIcon>
                <FiTrash2 color={themeColor} size={16}/>
              </ListItemIcon>
              <ListItemText primary="Delete" />
            </MenuItem>
          </MenuList>
        </Menu>
      )}
      { openSnackbar && (
        <Snackbar open={openSnackbar !== undefined } autoHideDuration={2000} onClose={handleCloseSnackbar}>
          <Alert
            onClose={handleCloseSnackbar}
            severity={openSnackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {openSnackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};
